/**
 * Single source of truth for "how should this scene's media be sized inside
 * the viewport". Used by both:
 *   - SceneMedia.jsx   → picks `object-cover` vs `object-contain` on the <video>/<img>
 *   - PublicViewer.jsx → mapPoint() converts normalized zone coords using
 *                        the matching computeFit(...) so polygons land where
 *                        the user actually sees the media.
 *
 * If these two ever diverge (different default, different precedence,
 * different value space), polygon zones will drift away from their visual
 * targets on any device whose viewport aspect ratio differs from the
 * media — most notably on phones. That was iter30 hotfix #1.
 *
 * Precedence:
 *   1. project.viewer_fit_mode  (when not "per_scene")
 *   2. scene.display_mode
 *   3. default: "contain"   ← full media visible, letterbox bars filled by
 *                             LetterboxBackdrop. Matches the original V1
 *                             intent and the ZoneEditor authoring grid.
 *
 * Returned value is always one of: "cover" | "contain".
 */
export function resolveSceneFit(scene, project) {
  const projFit = project?.viewer_fit_mode;
  if (projFit && projFit !== "per_scene") {
    return projFit === "cover" ? "cover" : "contain";
  }
  const sceneFit = scene?.display_mode;
  return sceneFit === "cover" ? "cover" : "contain";
}

const RATIO_PRESETS = { "16:9": 16 / 9, "9:16": 9 / 16, "4:3": 4 / 3, "3:4": 3 / 4, "1:1": 1, "21:9": 21 / 9 };

/**
 * Project cinematic target ratio → numeric width/height, or null when off.
 * When set, the viewer renders every scene inside a fixed box of this ratio
 * so transitions never resize/crop-jump between scenes.
 */
export function resolveTargetRatio(project) {
  const t = project?.target_ratio;
  if (!t) return null;
  if (t === "custom") {
    const c = Number(project?.target_ratio_custom);
    return Number.isFinite(c) && c > 0 ? c : null;
  }
  return RATIO_PRESETS[t] || null;
}

/** Largest box of ratio R that fits inside (vw,vh), centered. */
export function computeStageBox(vw, vh, ratio) {
  if (!ratio || !vw || !vh) return { w: vw, h: vh, left: 0, top: 0 };
  let w = vw, h = vw / ratio;
  if (h > vh) { h = vh; w = vh * ratio; }
  return { w: Math.round(w), h: Math.round(h), left: Math.round((vw - w) / 2), top: Math.round((vh - h) / 2) };
}
